import { styled } from 'goober';
import { useTranslation } from 'react-i18next';

import { useCurrentAccountData } from '@/hooks/use-current-account-data';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Stack } from '@/ui/line/line';
import { useUserAccount } from '@/user/use-user-account';

import { getSpotlightAccountDisplayName } from './get-spotlight-account-display-name';
import { SpotlightCompaniesListing } from './companies-listing/spotlight-companies-listing';
import { SpotlightInfoTooltip } from './spotlight-info-tooltip';
import { SpotlightSummary } from './summary/spotlight-summary';
import { useSpotlightPageTracking } from './tracking/use-spotlight-page-tracking';

export const Spotlight = () => {
  const { t } = useTranslation('spotlight');
  const account = useUserAccount();
  const accountName = getSpotlightAccountDisplayName(account);
  const { data: accountData } = useCurrentAccountData();

  useSpotlightPageTracking();

  return (
    <Container>
      <Header data-testid="spotlight-header">
        <Stack gap="20px">
          {accountData ? (
            <Logo
              alt={t('logoAlt', { accountName })}
              src={accountData.logo.url}
            />
          ) : null}
          <Flexbox gap="8px" alignItems="center">
            <H1>{t`header`}</H1>
            <SpotlightInfoTooltip
              title={t('tooltips.aboutDashboard.title')}
              description={t('tooltips.aboutDashboard.description', {
                accountName,
              })}
              dataTestId="spotlight-about-dashboard-tooltip"
            />
          </Flexbox>
        </Stack>
        <Description>{t('description', { accountName })}</Description>
      </Header>
      <SpotlightSummary />
      <SpotlightCompaniesListing />
    </Container>
  );
};

const Header = styled('header')`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const H1 = styled('h1')`
  ${({ theme }) => theme.typography.spotlight.header}
  margin: 0;
`;

const Logo = styled('img')`
  flex-shrink: 0;
  height: 56px;
  object-fit: contain;
  width: auto;
`;

const Description = styled('p')`
  ${({ theme }) => theme.typography.spotlight.description}
  color: ${({ theme }) => theme.colors.spotlight.text.secondary};
  margin: 0;
`;

const Container = styled('div')`
  box-sizing: border-box;
  display: grid;
  gap: 28px;
  width: 100%;
  padding-bottom: 24px;
`;
